import { useState, useEffect } from 'react';
import { useLazyGetRiskInsightsSitesQuery } from 'src/app/portfolio/state/api/riskInsightsEngineSlice';
import type { RiskInsightsSiteRow } from 'src/app/portfolio/state/api/riskInsightsSitesQuery';
import type { RiskDynamicsSite } from 'src/shared/components/charts/risk-dynamics-scatter-chart/RiskDynamicsScatterChart';
import cmsService from 'src/services/cms-service';
import { fetchPortfolioSitesForQuadrant } from './fetchPortfolioSitesForQuadrant';
import { toChartSites } from './toChartSites';

const PAGE_SIZE = 100;
const DEFAULT_MAX_PAGES = 20_000;
const CMS_SLUG = 'gnr-sites-scatterplot-limit';

function parseMaxPages(content: string): number | null {
    const text = content.replace(/<[^>]*>/g, '').trim(); // CMS content is HTML, e.g. <p>200</p>
    const num = parseInt(text, 10);
    return Number.isFinite(num) && num > 0 ? num : null;
}

export function usePortfolioSitesForQuadrant(
    orgId: string | undefined,
    at?: string
): {
    sites: RiskDynamicsSite[];
    isLoading: boolean;
    truncated: boolean;
    siteLimit: number;
    totalCount: number | null;
} {
    const [trigger] = useLazyGetRiskInsightsSitesQuery();
    const [sites, setSites] = useState<RiskDynamicsSite[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [truncated, setTruncated] = useState(false);
    const [siteLimit, setSiteLimit] = useState(DEFAULT_MAX_PAGES * PAGE_SIZE);
    const [totalCount, setTotalCount] = useState<number | null>(null);

    useEffect(() => {
        if (!orgId) return;

        let cancelled = false;
        let frameHandle: number | null = null;
        setIsLoading(true);
        setTotalCount(null);

        // Coalesce rapid per-page updates into at most one render per frame.
        const scheduleFrame: (cb: FrameRequestCallback) => number =
            typeof requestAnimationFrame === 'function' ? requestAnimationFrame : cb => window.setTimeout(() => cb(0), 16);
        const cancelFrame: (handle: number) => void =
            typeof cancelAnimationFrame === 'function' ? cancelAnimationFrame : window.clearTimeout;

        const run = async () => {
            const maxPagesPromise = cmsService
                .getArticleBySlug(CMS_SLUG)
                .then(article => (article && parseMaxPages(article.content)) ?? DEFAULT_MAX_PAGES);

            const fetchPage = async (page: number) => {
                const result = await trigger({ businessCentralId: orgId, page, pageSize: PAGE_SIZE, at }).unwrap();
                const pagination = result.organisation?.sites?.pagination;
                if (page === 1 && !cancelled) setTotalCount(pagination?.totalItems ?? null);
                return {
                    items: result.organisation?.sites?.items ?? [],
                    totalPages: pagination?.totalPages ?? 0,
                };
            };

            const accumulated: RiskInsightsSiteRow[] = [];
            const flushToState = () => {
                frameHandle = null;
                if (!cancelled) setSites(toChartSites(accumulated));
            };
            const onProgress = (pageItems: RiskInsightsSiteRow[]) => {
                accumulated.push(...pageItems);
                if (frameHandle === null) frameHandle = scheduleFrame(flushToState);
            };

            const { items, truncated: wasTruncated } = await fetchPortfolioSitesForQuadrant(fetchPage, maxPagesPromise, onProgress);

            if (!cancelled) {
                if (frameHandle !== null) cancelFrame(frameHandle);
                setSites(toChartSites(items));
                setTruncated(wasTruncated);
                setSiteLimit((await maxPagesPromise) * PAGE_SIZE);
                setIsLoading(false);
            }
        };

        run().catch(() => {
            if (!cancelled) setIsLoading(false);
        });

        return () => {
            cancelled = true;
            if (frameHandle !== null) cancelFrame(frameHandle);
        };
    }, [orgId, at, trigger]);

    return { sites, isLoading, truncated, siteLimit, totalCount };
}
